import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { MaterialModule } from '../../../shared/material.module';
import { CountryFlat } from '../../../models/country';
import { Helpers } from '../../../utilities/helpers';
import { LutService } from '../../../services/lut.service';
import {
  DeviationCheckService,
  deviationVariableLabel,
  capitalizationLabel
} from '../../../services/deviation-check.service';
import { DeviationCheckRowDto } from '../../../models/api/deviationCheckRowDto';
import { DeviationCheckVariable } from '../../../models/enums/deviationCheckVariable.enum';
import { Capitalization } from '../../../models/enums/capitalization.enum';

@Component({
  selector: 'app-divcheck',
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    NgxSpinnerModule,
    RouterLink
  ],
  templateUrl: './divcheck.component.html',
  styleUrl: './divcheck.component.scss'
})
export class DivCheckComponent implements OnInit {
  private fb = inject(FormBuilder);
  private lutService = inject(LutService);
  private deviationCheckService = inject(DeviationCheckService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);

  countries: CountryFlat[] = [];
  measurements = Helpers.measurements();

  readonly Variable = DeviationCheckVariable;
  readonly variableOptions: { value: DeviationCheckVariable; label: string }[] = [
    { value: DeviationCheckVariable.Audit, label: deviationVariableLabel(DeviationCheckVariable.Audit) },
    { value: DeviationCheckVariable.Board, label: deviationVariableLabel(DeviationCheckVariable.Board) },
    { value: DeviationCheckVariable.Ownership, label: deviationVariableLabel(DeviationCheckVariable.Ownership) },
    { value: DeviationCheckVariable.Compensation, label: deviationVariableLabel(DeviationCheckVariable.Compensation) },
    { value: DeviationCheckVariable.Family, label: deviationVariableLabel(DeviationCheckVariable.Family) },
    { value: DeviationCheckVariable.All, label: deviationVariableLabel(DeviationCheckVariable.All) },
    { value: DeviationCheckVariable.PerDirector, label: deviationVariableLabel(DeviationCheckVariable.PerDirector) }
  ];

  readonly Capitalization = Capitalization;
  readonly capitalizationOptions: { value: Capitalization; label: string }[] = [
    { value: Capitalization.Large, label: 'Large' },
    { value: Capitalization.Medium, label: 'Medium' },
    { value: Capitalization.Small, label: 'Small' },
    { value: Capitalization.Unknown, label: 'Unknown' }
  ];

  headers: string[] = [];
  rows: DeviationCheckRowDto[] = [];
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];
  sortBy = 'companyName';
  ascending = true;
  hasSearched = false;
  lastSearchVariable: DeviationCheckVariable | null = null;

  filtersForm = this.fb.group({
    variable: [DeviationCheckVariable.Audit as DeviationCheckVariable, Validators.required],
    countryId: ['', Validators.required],
    year: [''],
    capitalization: ['']
  });

  ngOnInit(): void {
    this.lutService.getVisibleCountries().subscribe({
      next: (countries) => this.countries = countries.sort((a, b) => a.name.localeCompare(b.name)),
      error: () => this.toast.error('Error loading countries')
    });
  }

  /** Columns shown before the dynamic deviation cells. */
  get prefixColumns(): { key: string; label: string; sortKey?: string }[] {
    const cols: { key: string; label: string; sortKey?: string }[] = [
      { key: 'companyName', label: 'Company', sortKey: 'companyName' },
      { key: 'country', label: 'Country' },
      { key: 'year', label: 'Year' },
      { key: 'firstYear', label: 'First Year' },
      { key: 'capitalization', label: 'Capitalization', sortKey: 'capitalization' }
    ];

    if (this.lastSearchVariable === DeviationCheckVariable.PerDirector) {
      cols.push(
        { key: 'auditChecked', label: 'Audit OK' },
        { key: 'corporateChecked', label: 'Corporate OK' },
        { key: 'compensationChecked', label: 'Compensation OK' },
        { key: 'csrChecked', label: 'CSR OK' },
        { key: 'accountingChecked', label: 'Accounting OK' },
        { key: 'directorName', label: 'Director', sortKey: 'directorName' }
      );
    } else {
      const single = this.singleLeaderColumn(this.lastSearchVariable);
      if (single) cols.push(single);
    }
    return cols;
  }

  get displayedColumns(): string[] {
    return [
      ...this.prefixColumns.map(c => c.key),
      ...this.headers.map((_, i) => 'cell_' + i)
    ];
  }

  getCapitalizationLabel(value: Capitalization | null | undefined): string {
    return capitalizationLabel(value);
  }

  cellClass(value: string): string {
    if (value === 'n.l.y') return 'sentinel sentinel-nly';
    if (value === 'c.y.e.') return 'sentinel sentinel-cye';
    if (value?.startsWith('Problem:')) return 'sentinel sentinel-problem';
    if (value && value.startsWith('-')) return 'value value-negative';
    if (value && value !== '0' && /^-?\d/.test(value)) return 'value value-positive';
    return '';
  }

  onSearch(): void {
    if (this.filtersForm.invalid) {
      this.filtersForm.markAllAsTouched();
      return;
    }
    this.currentPage = 1;
    this.loadResults();
  }

  onSortChange(sort: Sort): void {
    if (sort.direction) {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
      this.loadResults();
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadResults();
  }

  onExport(): void {
    if (this.filtersForm.invalid) {
      this.filtersForm.markAllAsTouched();
      return;
    }

    const req = this.buildRequest();
    if (!req) return;

    this.spinnerService.show();
    this.deviationCheckService.exportCsv(req).subscribe({
      next: (response) => {
        this.downloadCsv(response.body!, this.getFilename(response, `divcheck_${req.variable}`));
        this.spinnerService.hide();
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Error exporting deviation check');
      }
    });
  }

  cellValue(row: DeviationCheckRowDto, key: string): string {
    if (key.startsWith('cell_')) {
      const i = parseInt(key.substring('cell_'.length), 10);
      return row.cells?.[i] ?? '';
    }
    return '';
  }

  prefixCellRaw(row: DeviationCheckRowDto, key: string): unknown {
    switch (key) {
      case 'companyName': return row.companyName;
      case 'country': return row.country;
      case 'year': return row.year;
      case 'firstYear': return row.firstYearOfMeasurement ?? '';
      case 'capitalization': return capitalizationLabel(row.capitalization);
      case 'directorName': return row.directorName ?? '';
      case 'auditChecked': return row.auditChecked;
      case 'corporateChecked': return row.corporateChecked;
      case 'compensationChecked': return row.compensationChecked;
      case 'csrChecked': return row.csrChecked;
      case 'accountingChecked': return row.accountingChecked;
      case 'leaderFlag': return this.pickLeaderFlag(row);
      default: return '';
    }
  }

  isBoolean(key: string): boolean {
    return ['auditChecked', 'corporateChecked', 'compensationChecked',
      'csrChecked', 'accountingChecked', 'leaderFlag'].includes(key);
  }

  private buildRequest() {
    const v = this.filtersForm.value;
    if (!v.countryId || v.variable === null || v.variable === undefined) return null;

    return {
      variable: v.variable as DeviationCheckVariable,
      countryId: v.countryId,
      year: v.year ? +v.year : undefined,
      capitalization: v.capitalization !== '' && v.capitalization !== null && v.capitalization !== undefined
        ? +v.capitalization as Capitalization
        : undefined,
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending
    };
  }

  private loadResults(): void {
    const req = this.buildRequest();
    if (!req) return;

    this.spinnerService.show();
    this.deviationCheckService.search(req).subscribe({
      next: (response) => {
        this.headers = response.headers ?? [];
        this.rows = response.data ?? [];
        this.totalItems = response.totalCount;
        this.hasSearched = true;
        this.lastSearchVariable = req.variable;
        this.spinnerService.hide();
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Error running deviation check');
      }
    });
  }

  private singleLeaderColumn(variable: DeviationCheckVariable | null): { key: string; label: string } | null {
    switch (variable) {
      case DeviationCheckVariable.Audit: return { key: 'leaderFlag', label: 'Audit OK' };
      case DeviationCheckVariable.Board:
      case DeviationCheckVariable.Ownership:
      case DeviationCheckVariable.Family: return { key: 'leaderFlag', label: 'Corporate OK' };
      case DeviationCheckVariable.Compensation: return { key: 'leaderFlag', label: 'Compensation OK' };
      default: return null;
    }
  }

  private pickLeaderFlag(row: DeviationCheckRowDto): boolean | null {
    switch (this.lastSearchVariable) {
      case DeviationCheckVariable.Audit: return row.auditChecked;
      case DeviationCheckVariable.Board:
      case DeviationCheckVariable.Ownership:
      case DeviationCheckVariable.Family: return row.corporateChecked;
      case DeviationCheckVariable.Compensation: return row.compensationChecked;
      default: return null;
    }
  }

  private getFilename(response: { headers: { get(name: string): string | null } }, fallbackPrefix: string): string {
    const disposition = response.headers.get('Content-Disposition');
    if (disposition) {
      const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
      if (match?.[1]) {
        return decodeURIComponent(match[1]);
      }
    }
    return `${fallbackPrefix}_${new Date().toISOString().split('T')[0]}.csv`;
  }

  private downloadCsv(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
